/* eslint-disable no-console */
"use client";

import { useState } from "react";
import { Button } from "../inputs/button";
import { Copy, Check } from "lucide-react";
import { cn } from "../utils";

interface CopyButtonProps {
  /** The content to copy. Can be a string or a function that returns a string. */
  content: string | (() => string);
  /** The button variant */
  variant?: "default" | "outline" | "ghost";
  /** The button size */
  size?: "default" | "sm" | "lg" | "icon";
  /** Custom className for the button */
  className?: string;
  /** Custom text to show when copying */
  copyText?: string;
  /** Custom text to show when copied */
  copiedText?: string;
  /** Duration in milliseconds to show the "copied" state */
  copiedDuration?: number;
  /** Callback fired when copy is successful */
  onCopy?: () => void;
  /** Callback fired when copy fails */
  onCopyError?: (error: Error) => void;
}

export function CopyButton({
  content,
  variant = "outline",
  size = "sm",
  className,
  copyText = "Copy",
  copiedText = "Copied!",
  copiedDuration = 2000,
  onCopy,
  onCopyError,
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      const textToCopy = typeof content === "function" ? content() : content;
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), copiedDuration);
    } catch (error) {
      console.error("Failed to copy:", error);
      onCopyError?.(error as Error);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleCopy}
      className={cn("transition-all duration-200", className)}
    >
      {copied ? (
        <>
          <Check className="w-4 h-4 mr-2" />
          {copiedText}
        </>
      ) : (
        <>
          <Copy className="w-4 h-4 mr-2" />
          {copyText}
        </>
      )}
    </Button>
  );
}
